import React, { useState, useEffect } from 'react';
import { translate } from '@utils/i18n';
import { Button } from '@ui/atoms/Button';
import { Brushaway, BrushawayHeader, BrushawayBody, BrushawayFooter } from '@ui/molecules/Brushaway';
import { FileDropZone } from './components/FileDropZone';
import { SegmentedControl } from './components/SegmentedControl';
const namespace = 'features.inspections_attachments';
function emptyForm() {
    return {
        file: null,
        name: '',
        technicianName: '',
        certificationNumber: '',
        answer: null,
        showOnReport: true,
        includeInCoverLetter: false
    };
}
function formFromAttachment(a) {
    var _a, _b;
    return {
        file: null,
        name: a.name,
        technicianName: (_a = a.technicianName) !== null && _a !== void 0 ? _a : '',
        certificationNumber: (_b = a.certificationNumber) !== null && _b !== void 0 ? _b : '',
        answer: a.answer,
        showOnReport: a.showOnReport,
        includeInCoverLetter: a.includeInCoverLetter
    };
}
export function AttachmentBrushaway({ isOpen, attachment, hasCoverLetter, isSaving, onClose, onSave }) {
    const [form, setForm] = useState(emptyForm);
    const [fileError, setFileError] = useState(null);
    const [fileCleared, setFileCleared] = useState(false);
    useEffect(() => {
        setForm(attachment ? formFromAttachment(attachment) : emptyForm());
        setFileError(null);
        setFileCleared(false);
    }, [attachment, isOpen]);
    const isEdit = attachment != null;
    const opt = React.createElement("span", { className: "opt" }, translate('brushaway.optional', { namespace }));
    const set = (key, value) => setForm((prev) => (Object.assign(Object.assign({}, prev), { [key]: value })));
    const handleFile = (file) => {
        setFileError(null);
        set('file', file);
        if (file && !form.name)
            set('name', file.name.replace(/\.pdf$/i, ''));
    };
    const handleShowOnReport = (checked) => {
        set('showOnReport', checked);
        if (!checked)
            set('includeInCoverLetter', false);
    };
    const canSave = form.name.trim().length > 0 && (isEdit ? !fileCleared || !!form.file : !!form.file);
    const title = isEdit
        ? translate('brushaway.title_edit', { namespace })
        : translate('brushaway.title_add', { namespace });
    return (React.createElement(Brushaway, { controlledOpen: isOpen, onClose: onClose, closeOnClickOutside: true, minWidth: 480, className: "ia-scope" },
        React.createElement(BrushawayHeader, { title: title }),
        React.createElement(BrushawayBody, null,
            React.createElement("div", { className: "attachment-brushaway__fields" },
                React.createElement("div", { className: "bfield" },
                    React.createElement("label", null, translate('brushaway.file_label', { namespace })),
                    React.createElement(FileDropZone, { file: form.file, existingFileName: isEdit ? attachment.fileName : null, isCleared: fileCleared, error: fileError, onFile: handleFile, onClear: () => setFileCleared(true), onError: setFileError })),
                React.createElement("div", { className: "bfield" },
                    React.createElement("label", { htmlFor: "attachment-name" }, translate('brushaway.name_label', { namespace })),
                    React.createElement("input", { id: "attachment-name", type: "text", value: form.name, onChange: (e) => set('name', e.target.value) })),
                React.createElement("div", { className: "attachment-brushaway__two-col" },
                    React.createElement("div", { className: "bfield" },
                        React.createElement("label", { htmlFor: "attachment-technician" },
                            translate('brushaway.technician_label', { namespace }),
                            " ",
                            opt),
                        React.createElement("input", { id: "attachment-technician", type: "text", value: form.technicianName, onChange: (e) => set('technicianName', e.target.value) })),
                    React.createElement("div", { className: "bfield" },
                        React.createElement("label", { htmlFor: "attachment-cert" },
                            translate('brushaway.cert_label', { namespace }),
                            " ",
                            opt),
                        React.createElement("input", { id: "attachment-cert", type: "text", value: form.certificationNumber, onChange: (e) => set('certificationNumber', e.target.value) }))),
                React.createElement("div", { className: "bfield" },
                    React.createElement("label", null, translate('brushaway.result_label', { namespace })),
                    React.createElement(SegmentedControl, { value: form.answer, onChange: (v) => set('answer', v) })),
                React.createElement("div", { className: "checkrow" },
                    React.createElement("span", { className: `qmb-sw${form.showOnReport ? ' is-on' : ''}`, role: "switch", "aria-checked": form.showOnReport, "aria-label": translate('brushaway.show_on_report_label', { namespace }), tabIndex: 0, onClick: () => handleShowOnReport(!form.showOnReport), onKeyDown: (e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                handleShowOnReport(!form.showOnReport);
                            }
                        } }),
                    React.createElement("div", { className: "checkrow__meta" },
                        React.createElement("div", null, translate('brushaway.show_on_report_label', { namespace })),
                        React.createElement("div", { className: "help", dangerouslySetInnerHTML: { __html: translate('brushaway.show_on_report_helper', { namespace }) } }))),
                form.showOnReport && hasCoverLetter && (React.createElement("div", { className: "checkrow" },
                    React.createElement("span", { className: `qmb-sw${form.includeInCoverLetter ? ' is-on' : ''}`, role: "switch", "aria-checked": form.includeInCoverLetter, "aria-label": translate('brushaway.include_in_cover_letter_label', { namespace }), tabIndex: 0, onClick: () => set('includeInCoverLetter', !form.includeInCoverLetter), onKeyDown: (e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault();
                                set('includeInCoverLetter', !form.includeInCoverLetter);
                            }
                        } }),
                    React.createElement("div", { className: "checkrow__meta" },
                        React.createElement("div", null, translate('brushaway.include_in_cover_letter_label', { namespace })),
                        React.createElement("div", { className: "help", dangerouslySetInnerHTML: { __html: translate('brushaway.include_in_cover_letter_helper', { namespace }) } })))))),
        React.createElement(BrushawayFooter, null,
            React.createElement(Button, { variant: "secondary", onClick: onClose, disabled: isSaving }, translate('brushaway.cancel', { namespace })),
            React.createElement(Button, { variant: "primary", onClick: () => onSave(form), disabled: isSaving || !canSave },
                React.createElement("i", { className: "fa-solid fa-check", "aria-hidden": "true" }),
                isEdit ? translate('brushaway.save', { namespace }) : translate('brushaway.add', { namespace })))));
}
